<template>
    <div :id="rootId" class="calendar-widget">
        <div v-if="loading" class="loading active"></div>
        <!--<div class="widget-header port-corporate-background">
            <div>{{ calendarTitle }}</div>
        </div>-->
        <div class="calendar-controls">
            <button v-if="viewMode === 'grid'"
                    class="btn-list-view view-mode"
                    @click="toggleViewMode">
                <i class="fa fa-list"></i> List
            </button>
            <button v-else
                    class="btn-grid-view view-mode"
                    @click="toggleViewMode">
                <i class="fa fa-calendar-o"></i> Calendar
            </button>
            <button v-if="!isCurrentMonth"
                    class="btn-calendar-reset"
                    @click="resetToToday">
                <i class="fa fa-caret-left"></i> Today
            </button>
        </div>
        <div class="calendar-date-display">
            <div class="calendar-header-title">{{ selectedMonthName }} {{ selectedYear }}</div>
            <button class="btn-calendar-prev" @click="goToPreviousMonth"></button>
            <button class="btn-calendar-next" @click="goToNextMonth"></button>
        </div>
        <div class="calendar-day-title-wrap">
            <div v-for="day in dayNames" :key="day">{{ day }}</div>
        </div>
        <div class="calendar-day-wrap">
            <template v-for="(cell, idx) in calendarCells" :key="idx">
                <div v-if="cell.blank"
                     class="m-d calendar-day-blank">
                    <div class="calendar-day-number"></div>
                </div>
                <a v-else
                   href="#"
                   class="m-d calendar-day calendar-day-event"
                   :class="{ 'calendar-today': cell.isToday }"
                   :data-number="cell.day"
                   @click.prevent="showDayEvents(cell.day)">
                    <div class="calendar-day-number">{{ cell.day }}</div>
                    <div class="calendar-indicator-wrap">
                        <div v-for="event in cell.events"
                             :key="event.eventId"
                             class="calendar-event-indicator calendar-event-title"
                             :data-eventid="event.eventId"
                             :data-eventcolor="event.eventColor"
                             :style="{ background: event.eventColor, textDecoration: event.cancelled ? 'line-through' : '' }"
                             :title="event.eventTitle">
                            {{ event.eventTitle }}
                            <span v-if="event.cancelled" class="badge badge-danger ml-2">CANCELED</span>
                        </div>
                    </div>
                </a>
            </template>
        </div>
        <div class="calendar-event-list" :class="{ active: viewMode === 'list' }">
            <div v-if="showViewPrior"
                 class="calendar-list-view-prior"
                 @click="showPriorEvents">View Prior Events</div>
            <div v-for="event in listedEvents"
                 :key="event.eventId"
                 class="listed-event"
                 :data-number="event.startDay"
                 :data-eventid="event.eventId"
                 :data-eventcolor="event.eventColor"
                 :style="{ borderLeft: '10px solid ' + event.eventColor }"
                 :title="event.eventTitle"
                 @click="showListEvent(event)">
                <span :style="{ color: event.eventColor }">{{ event.eventTitle }}</span>
                <span v-if="event.cancelled" class="badge badge-danger ml-2">CANCELED</span>
                <div class="calendar-list-time-start">{{ event.displayDate }}</div>
            </div>
        </div>
        <div class="calendar-event-detail mini-scroll" :class="{ active: eventDetailActive }">
            <div>
                <div class="calendar-event-detail-close" @click="closeEventDetail"></div>
                <div v-for="event in eventDetails" :key="event.eventId">
                    <div class="calendar-event-detail-title">
                        <span :style="{ color: event.eventColor }">{{ event.eventTitle }}</span>
                        <span v-if="event.cancelled" class="badge badge-danger ml-2">CANCELED</span>
                    </div>
                    <div class="calendar-event-detail-time">{{ event.displayDate }}</div>
                    <div class="calendar-event-detail-description" v-html="event.eventDescription"></div>
                    <div class="calendar-event-location" v-if="event.displayEventLocation" v-html="event.displayEventLocation"></div>
                    <div class="calendar-event-contact" v-if="event.displayContactInfo" v-html="event.displayContactInfo"></div>
                    <div class="calendar-event-url" v-if="event.eventUrl" v-html="event.eventUrl"></div>
                    <hr />
                    <div>
                        <div class="calendar-deep-link">
                            <a :href="`/Calendars/${props.calendarId}/${event.eventId}`">direct link</a>
                        </div>
                        <!-- ADD TO CALENDAR -->
                        <div v-if="atc" v-html="atc.render(event)"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, watch, onMounted } from 'vue'
    import { useLocalStorage } from '@vueuse/core'
    import dayjs from 'dayjs'
    import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
    import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
    import _ from 'lodash'
    import "../../../../css/components/port-calendar.less";
    import { AddToCalendar } from "../../port-addtocalendar";

    dayjs.extend(isSameOrAfter);
    dayjs.extend(isSameOrBefore);

    // Props
    const props = defineProps({
        widgetId: { type: String, required: true },
        calendarId: { type: Number, default: 0 },
        calendarTitle: { type: String, default: 'Calendar' },
        weekStart: { type: String, default: 'Sun' },
        mode: { type: String, default: 'grid' }, // 'grid' or 'list'
        initialData: { type: Array, default: undefined },
        jsonUrl: { type: String, default: '' },
        eventDetailsUrl: { type: String, default: '' },
        eventUrl: { type: String, default: '' },
        target: { type: String, default: '' },
        eventList: { type: Boolean, default: true },
        maxWidth: { type: [Boolean, Number], default: false },
        setWidth: { type: [Boolean, Number], default: false },
        stylePast: { type: Boolean, default: false },
        disablePast: { type: Boolean, default: false },
        displayEventsInHeader: { type: Boolean, default: true },
        isAdmin: { type: Boolean, default: false },
        clearCache: { type: Boolean, default: false },
        debug: { type: Boolean, default: false }
    });

    // State
    const today = dayjs()
    const currentMonth = ref(today.month() + 1);
    const currentYear = ref(today.year());
    const currentDay = ref(today.date());
    const selectedMonth = ref(currentMonth.value);
    const selectedYear = ref(currentYear.value);
    const viewMode = useLocalStorage('CalendarViewMode', props.mode);
    const loading = ref(false);
    const eventDetailActive = ref(false);
    const eventDetails = ref([]);
    const showViewPrior = ref(false);
    const priorEventsShown = ref(false);

    // Data
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const baseDayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

    const dayNames = computed(() => {
        const start = baseDayNames.findIndex(
            d => d.toLowerCase() === props.weekStart.toLowerCase()
        );
        if (start === -1) return baseDayNames;
        return [...baseDayNames.slice(start), ...baseDayNames.slice(0, start)];
    });

    const selectedMonthName = computed(() => monthNames[selectedMonth.value - 1]);
    const isCurrentMonth = computed(() => selectedMonth.value === currentMonth.value && selectedYear.value === currentYear.value);

    const rootId = computed(() => `calendar-widget-${props.widgetId}`);

    const atc = computed(() => new AddToCalendar({
        events: eventDetails.value,
        identifier: rootId.value
    }));

    // Events cache: { 'YYYY-M': [events] }
    const monthsEventsMap = ref({});
    const selectedMonthEvents = computed(() => {
        const key = `${selectedYear.value}-${selectedMonth.value}`;
        return monthsEventsMap.value[key] || [];
    })

    function daysInMonth(month, year) {
        // Subtract 1 from month to match JS Date zero-based month
        return dayjs(new Date(year, month - 1, 1)).daysInMonth();
    }

    function getFirstDayOfWeek(month, year) {
        const weekStartIndex = baseDayNames.findIndex(
            d => d.toLowerCase() === props.weekStart.toLowerCase()
        );
        // Subtract 1 from month to match JS Date zero-based month
        const firstDay = new Date(year, month - 1, 1).getDay(); // 0 = Sunday, 1 = Monday, etc.
        // Calculate offset based on weekStartIndex
        return (firstDay - weekStartIndex + 7) % 7;
    }

    // Calendar grid cells (days + blanks), updated after events are loaded
    const calendarCells = ref([]);

    function updateCalendarCells() {
        const blanks = getFirstDayOfWeek(selectedMonth.value, selectedYear.value);
        const days = daysInMonth(selectedMonth.value, selectedYear.value);
        const cells = [];
        for (let i = 0; i < blanks; i++) {
            cells.push({ blank: true });
        }
        for (let day = 1; day <= days; day++) {
            const isToday =
                day === currentDay.value &&
                selectedMonth.value === currentMonth.value &&
                selectedYear.value === currentYear.value;
            const events = getEventsForDay(day);
            cells.push({ blank: false, day, isToday, events });
        }
        // Fill end blanks to complete weeks
        while (cells.length % 7 !== 0) {
            cells.push({ blank: true });
        }
        calendarCells.value = cells;
    }

    // List view events (flattened, sorted)
    const listedEvents = computed(() => {
        let events = [];
        for (const day of selectedMonthEvents.value) {
            for (const event of day.events) {
                const converted = convertEvent(event);
                events.push(converted);
            }
        }
        // Sort by start date/time
        events = _.sortBy(events, e => dayjs(e.startDate + ' ' + e.startTime).toDate());
        // Hide prior events unless "View Prior" clicked
        if (!priorEventsShown.value && isCurrentMonth.value) {
            const todayDate = today.startOf('day');
            events = events.filter(e => {
                const end = dayjs(e.endDate);
                return end.isSameOrAfter(todayDate);
            })
            showViewPrior.value = true;
        } else {
            showViewPrior.value = false;
        }
        return events;
    });

    // Fetch events (from prop or AJAX), using monthsEventsMap as cache
    async function loadEvents() {
        loading.value = true;
        const key = `${selectedYear.value}-${selectedMonth.value}`;
        // If already cached, just use it
        if (monthsEventsMap.value[key]) {
            loading.value = false;
            updateCalendarCells();
            return;
        }
        let data = [];
        // If initialData and current month, use it
        if (props.initialData && Array.isArray(props.initialData) &&
            selectedMonth.value === currentMonth.value &&
            selectedYear.value === currentYear.value) {
            data = props.initialData;
        } else if (props.jsonUrl && props.calendarId) {
            try {
                const url = new URL(props.jsonUrl, window.location.origin);
                url.searchParams.set('calendarId', props.calendarId);
                url.searchParams.set('month', selectedMonth.value);
                url.searchParams.set('year', selectedYear.value);
                if (props.clearCache) {
                    url.searchParams.set('x', Math.ceil(Math.random() * 999999));
                }
                const resp = await fetch(url, { cache: props.clearCache ? 'reload' : 'default' });
                data = await resp.json();
            } catch (e) {
                if (props.debug) {
                    console.error('Error loading events', e);
                }
                data = [];
            }
        }
        // Group and cache
        const grouped = generateCurrentDayEvents(data);
        monthsEventsMap.value[key] = grouped;
        updateCalendarCells();
        loading.value = false;
    }

    // Returns grouped events array for a month
    function generateCurrentDayEvents(events) {
        // Group events by day number
        const map = {};
        for (const event of events) {
            const dayNumber = dayjs(event.StartDate).date();
            if (!map[dayNumber]) {
                map[dayNumber] = [];
            }
            map[dayNumber].push(event);
        }
        return Object.entries(map).map(([day, events]) => ({
            day: Number(day),
            events
        }))
    }

    function getEventsForDay(dayNumber) {
        const found = selectedMonthEvents.value.find(e => e.day === dayNumber);
        if (found) {
            return found.events.map(convertEvent);
        }
        return [];
    }

    // HACK: this must match convertEvent's schema in calendar-utilities.js in order to match the behavior for Add To Calendar
    // TODO: just use the one in utilities
    function convertEvent(event) {
        // Use dayjs for date parsing
        const djStart = dayjs(event.StartDate);
        const djEnd = dayjs(event.EndDate);
        const start = djStart.toISOString();
        const end = djEnd.toISOString();
        const startDate = djStart.format('M/D/YYYY');
        const endDate = djEnd.format('M/D/YYYY');
        const startMonth = djStart.month() + 1;
        const startDay = djStart.date();
        const startYear = djStart.year();
        const endMonth = djEnd.month() + 1;
        const endDay = djEnd.date();
        const endYear = djEnd.year();
        const eventTitle = event.Name;
        const eventColor = event.EventColor;
        const eventId = event.Id;
        const cancelled = event.Cancelled;
        const startTime = event.StartTime;
        const endTime = event.EndTime;
        // Compose displayDate
        let displayDate = '';
        if (startDate === endDate && startTime === endTime) {
            displayDate = `${startDate}, ${startTime}`;
        } else if (startDate === endDate) {
            displayDate = `${startDate}, ${startTime} - ${endTime}`;
        } else {
            displayDate = `${startDate} - ${endDate}, ${startTime} - ${endTime}`;
        }
        // Location
        const locationName = event.LocationName || '';
        const locationAddress1 = event.locationAddress1 || '';
        const locationAddress2 = event.locationAddress2 || '';
        let displayEventLocation = '';
        if (locationName && locationAddress1) {
            const mapUri = `http://maps.google.com/?q=${encodeURI(locationAddress1)} ${encodeURI(locationAddress2)}`;
            displayEventLocation = `<b>Location</b>: <a href='${mapUri}' target='_blank'>${locationName}</a>`;
        } else if (locationName) {
            displayEventLocation = `<b>Location</b>: ${locationName}`;
        }
        // Contact
        const contactName = event.ContactName || '';
        const contactEmail = event.ContactEmail || '';
        const contactPhone = event.ContactPhone || '';
        let displayContactInfo = ''
        if (contactEmail) {
            displayContactInfo = `<b>Contact</b>: <a href='mailto:${contactEmail}?subject=${eventTitle}'>${contactName}</a>`;
        } else if (contactName) {
            displayContactInfo = `<b>Contact</b>: ${contactName}`;
        }
        if (contactPhone) {
            displayContactInfo += ` - <a href='tel:${contactPhone.replace(/\./g, '')}'>${contactPhone}</a>`;
        }
        // Event URL
        let eventUrl = ''
        if (event.Url) {
            eventUrl = `<p><b>Url</b>: <a href="${event.Url}" target="_blank">${event.Url}</a></p>`;
        }
        // Description
        let eventDescription = event.Description || ''
        if (eventDescription.length > 1000) {
            eventDescription = eventDescription.substr(0, 1000) + '...';
        }

        return {
            eventId,
            start,
            startDate,
            startTime,
            startMonth,
            startDay,
            startYear,
            end,
            endDate,
            endTime,
            endDay,
            endMonth,
            endYear,
            eventUrl,
            eventTitle,
            cancelled,
            eventColor,
            eventDescription,
            locationName,
            locationAddress1,
            locationAddress2,
            contactName,
            contactEmail,
            contactPhone,
            displayDate,
            displayEventLocation,
            displayContactInfo
        }
    }

    // Navigation
    function goToPreviousMonth() {
        if (selectedMonth.value === 1) {
            selectedMonth.value = 12;
            selectedYear.value -= 1;
        } else {
            selectedMonth.value -= 1;
        }
    }
    function goToNextMonth() {
        if (selectedMonth.value === 12) {
            selectedMonth.value = 1;
            selectedYear.value += 1;
        } else {
            selectedMonth.value += 1;
        }
    }
    function resetToToday() {
        selectedMonth.value = currentMonth.value;
        selectedYear.value = currentYear.value;
    }
    function toggleViewMode() {
        closeEventDetail()
        viewMode.value = viewMode.value === 'grid' ? 'list' : 'grid';
    }

    // Event detail
    function showDayEvents(day) {
        const events = getEventsForDay(day);
        eventDetails.value = events;
        if (events.length > 0) {
            eventDetailActive.value = true;
        }
    }

    function showListEvent(event) {
        eventDetails.value = [event];
        eventDetailActive.value = true;
    }
    function closeEventDetail() {
        eventDetailActive.value = false;
        eventDetails.value = [];
    }
    function showPriorEvents() {
        priorEventsShown.value = true;
    }

    // Watchers
    watch([selectedMonth, selectedYear], () => {
        closeEventDetail();
        priorEventsShown.value = false;
        loadEvents();
    })

    watch(() => props.calendarId, newVal => {
        closeEventDetail();
        priorEventsShown.value = false;
        monthsEventsMap.value = [];
        loadEvents();
    });

    // On mount, populate cache for current month if initialData exists
    onMounted(() => {
        const key = `${currentYear.value}-${currentMonth.value}`;
        loadEvents();
    })
</script>

<style scoped>
    .calendar-widget {
        position: relative;
    }

    .loading.active {
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        background: rgba(255,255,255,0.7);
        z-index: 10;
    }

    .calendar-controls {
        display: flex;
        gap: 0.5em;
        margin-bottom: 0.5em;
        justify-content: space-between;
    }

    .calendar-day-title-wrap {
        display: flex;
        justify-content: space-between;
        font-weight: bold;
        margin-bottom: 0.25em;
    }

    .calendar-day-wrap {
        display: flex;
        flex-wrap: wrap;
    }

    .calendar-day,
    .calendar-day-blank {
        width: calc(100% / 7);
        min-height: 60px;
        box-sizing: border-box;
        border: 1px solid #eee;
        position: relative;
    }

    .calendar-today {
        background: #e0f7fa;
    }

    .calendar-event-indicator {
        margin: 2px 0;
        padding: 2px 4px;
        border-radius: 2px;
        font-size: 0.9em;
        display: block;
        color: #fff;
    }

    .calendar-event-title {
        font-weight: bold;
    }

/*    .calendar-event-list {
        margin-top: 1em;
        display: none;
    }

        .calendar-event-list.active {
            display: block;
        }
*/
    .listed-event {
        margin-bottom: 0.5em;
        padding: 0.5em;
        background: #fafafa;
        border-radius: 3px;
        cursor: pointer;
    }

    .prior-date {
        display: none;
    }

    .calendar-list-view-prior {
        cursor: pointer;
        color: #1976d2;
        margin-bottom: 0.5em;
    }

    .calendar-event-detail {
        /*display: none;*/
        background: #fff;
        border: 1px solid #eee;
        padding: 1em;
        /*margin-top: 1em;*/
        border-radius: 4px;
        /*max-height: 300px;*/
        overflow-y: auto;
    }

/*        .calendar-event-detail.active {
            display: block;
        }*/

    .calendar-event-detail-close {
        float: right;
        cursor: pointer;
        font-size: 1.2em;
        margin-bottom: 0.5em;
    }
</style>
